import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Icon } from "@/components/ui/Icon";
import { LANG_NATIVE_LABEL, SUPPORTED_LNGS, type SupportedLng } from "@/lib/i18n";
import { pickBrowserLang } from "@/lib/i18n/browserLang";
import { useLang } from "@/lib/i18n/useLang";
import { readKey } from "@/lib/safeAccess";

/**
 * 로그인 전 화면 언어 선택(지구본 메뉴) — 오른쪽 위 고정.
 *
 * 고른 언어는 바로 화면에 적용하고 브라우저에 기억함(회원가입·비밀번호 찾기로 옮겨도 유지).
 * 로그인하면 그 언어를 계정 언어로 저장함(useApplyServerLang). 로그인 후 화면에서는 쓰지 않음 — 사용자 설정이 창구.
 */
export function LangMenu() {
  const { t } = useTranslation();
  const { lang, applyLang } = useLang();
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  // 바깥 클릭·Esc 로 닫힘
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!boxRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  function choose(next: SupportedLng) {
    pickBrowserLang(next);
    applyLang(next);
    setOpen(false);
  }

  return (
    <div ref={boxRef} className="lang-menu">
      <button
        type="button"
        className="lang-menu-btn"
        aria-label={t("common.language")}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <Icon name="globe" size={20} />
        <Icon name="chevD" size={12} />
      </button>
      {open && (
        <div className="dropdown lang-menu-list" role="menu">
          {SUPPORTED_LNGS.map((code) => (
            <button
              key={code}
              type="button"
              role="menuitemradio"
              aria-checked={code === lang}
              className={code === lang ? "lang-menu-item on" : "lang-menu-item"}
              onClick={() => choose(code)}
            >
              <span>{readKey(LANG_NATIVE_LABEL, code)}</span>
              {code === lang && <Icon name="check" size={16} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
